import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Palette, Eye, Settings2, Download, Upload } from 'lucide-react'
import { useBrand } from '@/contexts/BrandContext'

export function BrandGuidelinesTab() {
  const { settings, updateSettings, toggleComponent, isComponentBranded } = useBrand()

  const brandColors = [
    { name: 'Midnight', value: '#061522', description: 'Primary text / dark backgrounds' },
    { name: 'Chalk', value: '#FFF6E6', description: 'Base page background' },
    { name: 'Holiday', value: '#55A4DC', description: 'Primary CTA / links' },
    { name: 'Ascot', value: '#FF794F', description: 'Secondary CTA / promo' },
    { name: 'Pine', value: '#126658', description: 'Data accents / dividers' },
    { name: 'Baby', value: '#BBDBF2', description: 'Alt section background' },
    { name: 'Peach', value: '#FFDDC8', description: 'Alt section background' },
    { name: 'Mint', value: '#CBF6EE', description: 'Alt section background' }
  ]

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'brand-settings.json'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleImportSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const importedSettings = JSON.parse(e.target?.result as string)
          updateSettings(importedSettings)
        } catch (error) {
          console.error('Failed to import settings:', error)
        }
      }
      reader.readAsText(file)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Palette className="h-6 w-6" />
            Brand Guidelines Management
          </CardTitle>
          <p className="text-muted-foreground">
            Configure and manage your brand guidelines across the application. Enable new branding system
            and control individual component styling.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global Brand Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="global-brand" className="text-base font-medium">
                Enable Brand Guidelines System
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Activate the new brand guidelines across the application
              </p>
            </div>
            <Switch
              id="global-brand"
              checked={settings.enabled}
              onCheckedChange={(checked) => updateSettings({ enabled: checked })}
            />
          </div>

          {/* Export/Import Settings */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportSettings} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export Settings
            </Button>
            <div className="relative">
              <input
                type="file"
                accept=".json"
                onChange={handleImportSettings}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Button variant="outline" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Import Settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Palette */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Brand Color Palette
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {brandColors.map((color) => (
              <div key={color.name} className="space-y-2">
                <div
                  className="w-full h-16 rounded-lg border"
                  style={{ backgroundColor: color.value }}
                />
                <div>
                  <p className="font-medium text-sm">{color.name}</p>
                  <p className="text-xs text-muted-foreground">{color.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{color.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Component Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Component-Level Controls
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Enable or disable brand guidelines for specific component types
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Global Override */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="global-components" className="font-medium">
                Apply to All Components
              </Label>
              <p className="text-xs text-muted-foreground">Override all individual component settings</p>
            </div>
            <Switch
              id="global-components"
              checked={settings.components.global}
              onCheckedChange={() => toggleComponent('global')}
              disabled={!settings.enabled}
            />
          </div>

          <Separator />

          {/* Individual Component Controls */}
          {Object.entries(settings.components).filter(([key]) => key !== 'global').map(([component, enabled]) => (
            <div key={component} className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label htmlFor={component} className="font-medium capitalize">
                  {component}
                </Label>
                <p className="text-xs text-muted-foreground">
                  Apply brand guidelines to {component} components
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isComponentBranded(component as keyof typeof settings.components) && (
                  <Badge variant="secondary" className="text-xs">Active</Badge>
                )}
                <Switch
                  id={component}
                  checked={enabled}
                  onCheckedChange={() => toggleComponent(component as keyof typeof settings.components)}
                  disabled={!settings.enabled || settings.components.global}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Component Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Component Preview
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Preview how components look with brand guidelines applied
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Button Previews */}
          <div className="space-y-3">
            <h4 className="font-medium">Buttons</h4>
            <div className="flex gap-3 flex-wrap">
              <Button 
                className={isComponentBranded('buttons') ? 'brand-btn-primary' : ''}
                variant={isComponentBranded('buttons') ? undefined : 'default'}
              >
                Primary Button
              </Button>
              <Button 
                className={isComponentBranded('buttons') ? 'brand-btn-secondary' : ''}
                variant={isComponentBranded('buttons') ? undefined : 'secondary'}
              >
                Secondary Button
              </Button>
              <Button 
                className={isComponentBranded('buttons') ? 'brand-btn-ghost' : ''}
                variant={isComponentBranded('buttons') ? undefined : 'outline'}
              >
                Ghost Button
              </Button>
            </div>
          </div>

          {/* Card Previews */}
          <div className="space-y-3">
            <h4 className="font-medium">Cards</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className={isComponentBranded('cards') ? 'brand-card' : ''}>
                <CardContent className="p-4">
                  <p className="text-sm">Default Card</p>
                </CardContent>
              </Card>
              <Card className={isComponentBranded('cards') ? 'brand-card mint' : ''}>
                <CardContent className="p-4">
                  <p className="text-sm">Mint Card</p>
                </CardContent>
              </Card>
              <Card className={isComponentBranded('cards') ? 'brand-card peach' : ''}>
                <CardContent className="p-4">
                  <p className="text-sm">Peach Card</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}